import React, { useState, useEffect } from 'react';
import { Modal, InputText, Button } from 'schola-components';
import PropTypes from 'prop-types';
import './password-reset-form.scss';
import { getProfile } from 'util/auth';

import { updateUserPassword } from 'redux/user/user-actions';
import { useDispatch } from 'react-redux';
import { View16, ViewOff16, Checkmark16, Close16, ArrowLeft32 } from '@carbon/icons-react';
import { t, tString } from 'components/translation/Translation';

import { useResponsiveRender } from 'hooks/useResponsiveRender';

const PasswordResetForm = ({ isVisible, onClose, passwordAlertStatusChange }) => {
  const dispatch = useDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form, setForm] = useState({
    password: '',
    password_confirmation: '',
  });
  const [errors, setErrors] = useState({});
  const [isPasswordShown, setIsPasswordShown] = useState(false);
  const [isPasswordConfirmationShown, setIsPasswordConfirmationShown] = useState(false);
  const { renderMobile: mobileRender } = useResponsiveRender({ breakpoint: 565 });

  const validate = (form) => {
    let tempErrors = {};
    if (!form.password) {
      tempErrors.password = tString('parent:password_reset.password_required');
    }
    if (!form.password_confirmation) {
      tempErrors.password_confirmation = tString('parent:password_reset.password_confirmation_required');
    } else if (form.password_confirmation !== form.password) {
      tempErrors.password_confirmation = tString('parent:password_reset.password_not_the_same');
    }
    if (!conditionCheck('length') || !conditionCheck('containsNumber') || !conditionCheck('containsUppercase')) {
      tempErrors.password = tString('parent:password_reset.password_not_meet_conditions');
    }

    setErrors(tempErrors);
    return tempErrors;
  };

  const conditionCheck = (condition) => {
    const regexNumberCheck = /\d/;
    const regexUppercaseCheck = /[A-Z]/;

    if (condition === 'length') {
      return form.password.length >= 8;
    }

    if (condition === 'containsNumber') {
      return regexNumberCheck.test(form.password);
    }

    if (condition == 'containsUppercase') {
      return regexUppercaseCheck.test(form.password);
    }
  };

  const sendForm = async () => {
    let _profile = getProfile();
    const res = await dispatch(
      updateUserPassword(_profile.sub, {
        password: form.password,
      })
    );

    if (res.response.ok) {
      passwordAlertStatusChange();
      onClose();
    } else {
      const error = await res.response.statusText;
      console.log(error);
    }
  };

  const handleInput = (e) => {
    e.persist();
    setForm({
      ...form,
      [e.target.name]: e.target.value,
    });
  };

  const onSubmit = (e) => {
    e.preventDefault();
    let temp = validate(form);
    setErrors(temp);
    if (Object.keys(temp).length === 0) setIsSubmitting(true);
  };

  useEffect(() => {
    if (Object.keys(errors).length === 0 && isSubmitting) {
      sendForm();
    }
  }, [errors]);

  return (
    <Modal
      isVisible={isVisible}
      className="password-reset-modal form_wrapper"
      data-testid="password-reset-formModal"
      onClose={mobileRender ? '' : onClose}>
      {mobileRender && (
        <div className="password-reset-modal__header spacing-py-16  spacing-pr-16">
          <span>
            <ArrowLeft32
              className="cursor-pointer"
              onClick={() => {
                onClose();
              }}
            />
          </span>
          <h4 className="heading-4 mobile-title">{t('parent:password_reset.title')}</h4>
          <p className="spacing-mt-12" />
        </div>
      )}
      <form className="password-reset-form" onSubmit={onSubmit}>
        {!mobileRender && <h4 className="heading-4 spacing-mb-24 ">{t('parent:password_reset.title')}</h4>}
        <h4 className={mobileRender ? 'body-regular spacing-mb-24 spacing-mt-24' : 'body-regular spacing-mb-24'}>
          {t('parent:password_reset.subtitle')}
        </h4>
        <div className="password-reset-form_password">
          <InputText
            label={t('parent:password_reset.new_password')}
            type={!isPasswordShown ? 'password' : 'text'}
            placeholder=""
            id="password"
            name="password"
            value={form.password}
            onChange={handleInput}
            error={errors?.password}
            errorMessage={errors?.password}
          />
          <div
            onClick={() => setIsPasswordShown(!isPasswordShown)}
            className={
              !errors?.password ? 'password-reset-form_password_icon' : 'password-reset-form_password_icon-error'
            }>
            {isPasswordShown ? <View16 fill="#6E7B91" /> : <ViewOff16 fill="#6E7B91" />}
          </div>
        </div>

        <ul>
          {conditionCheck('length') === true ? (
            <li className="password-reset-form_condition-ok">
              <Checkmark16 />
              {t('parent:password_reset.at_least_char')}
            </li>
          ) : (
            <li className="password-reset-form_condition">
              <Close16 />
              {t('parent:password_reset.at_least_char')}
            </li>
          )}

          {conditionCheck('containsNumber') === true ? (
            <li className="password-reset-form_condition-ok">
              <Checkmark16 />
              {t('parent:password_reset.at_least_number')}
            </li>
          ) : (
            <li className="password-reset-form_condition">
              <Close16 />
              {t('parent:password_reset.at_least_number')}
            </li>
          )}

          {conditionCheck('containsUppercase') === true ? (
            <li className="password-reset-form_condition-ok">
              <Checkmark16 />
              {t('parent:password_reset.at_least_uppercase')}
            </li>
          ) : (
            <li className="password-reset-form_condition">
              <Close16 />
              {t('parent:password_reset.at_least_uppercase')}
            </li>
          )}
        </ul>
        <div className="spacing-mb-32 password-reset-form_password">
          <InputText
            label={t('parent:password_reset.confirm_new_password')}
            type={!isPasswordConfirmationShown ? 'password' : 'text'}
            placeholder=""
            id="password_confirmation"
            name="password_confirmation"
            value={form.password_confirmation}
            onChange={handleInput}
            error={errors?.password_confirmation}
            errorMessage={errors?.password_confirmation}
          />
          <div
            onClick={() => setIsPasswordConfirmationShown(!isPasswordConfirmationShown)}
            className={
              !errors?.password_confirmation
                ? 'password-reset-form_password_icon'
                : 'password-reset-form_password_icon-error'
            }>
            {isPasswordConfirmationShown ? <View16 fill="#6E7B91" /> : <ViewOff16 fill="#6E7B91" />}
          </div>
        </div>

        <div
          className={
            !mobileRender
              ? 'password-reset-form_button-container password-reset-form_button-container_desktop'
              : 'password-reset-form_button-container'
          }>
          {!mobileRender && (
            <Button
              className="spacing-mt-32 light-button"
              name="cancel"
              onClick={onClose}
              color="secondarylegacy"
              fullWidth
              size="large">
              {t('parent:password_reset.cancel')}
            </Button>
          )}
          <Button
            className="spacing-mt-32 darker-button"
            name="save"
            type="submit"
            color="secondarylegacy"
            fullWidth
            size="large">
            {t('parent:password_reset.save_password')}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

PasswordResetForm.propTypes = {
  profile: PropTypes.object,
  isVisible: PropTypes.bool,
  onClose: PropTypes.func,
};

export default PasswordResetForm;
